'use client';

import { useEffect } from 'react';
import Bugsnag from '@bugsnag/js';
import { recoverFromChunkLoadError } from '@/app/utils/chunk-recovery';

interface GlobalErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function GlobalError({ error, reset }: GlobalErrorProps) {
  useEffect(() => {
    // A failed chunk load (stale asset after a deploy, or a network timeout) is
    // recoverable by reloading. When we reload, skip the report below — it's an
    // expected, self-healing condition, not an actionable bug.
    if (recoverFromChunkLoadError(error)) return;

    // Manually report to Bugsnag since this is outside the BugsnagProvider
    if (Bugsnag.isStarted()) {
      Bugsnag.notify(error, (event) => {
        event.addMetadata('context', {
          source: 'global-error',
          digest: error.digest,
        });
      });
    }
    console.error('GlobalError caught:', error);
  }, [error]);

  return (
    <html lang="en">
      <body>
        <div className="min-h-screen flex items-center justify-center bg-white">
          <div className="text-center p-8 max-w-md">
            <h1 className="text-2xl font-bold text-zinc-800 mb-4">
              Something went wrong
            </h1>
            <p className="text-zinc-600 mb-6">
              We apologize for the inconvenience. Please try again.
            </p>
            <div className="space-x-4">
              <button
                onClick={reset}
                className="bg-red-500 text-white px-6 py-2 rounded hover:opacity-90"
              >
                Try Again
              </button>
              <button
                onClick={() => window.location.reload()}
                className="border border-zinc-300 text-zinc-700 px-6 py-2 rounded hover:bg-zinc-50"
              >
                Refresh Page
              </button>
            </div>
          </div>
        </div>
      </body>
    </html>
  );
}
